import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { MyFranchiseDTO, PurchaseRowDTO } from '@bifeai/shared';
import { Badge, Button, Card, CardBody, EmptyState, Input, Kpi, PageHeader, Select, Skeleton } from '@bifeai/ui';
import { Plus, ReceiptText } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Toolbar } from '../../components/inventory';
import { api } from '../../lib/api';
import { useSuppliers } from '../../lib/catalog';
import { formatDate, formatMoney } from '../../lib/format';
import { useSession } from '../../lib/session';
import { daysUntil } from '../../lib/units';

function monthStart(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

export function PurchasesPage() {
  const navigate = useNavigate();
  const { multiBranch, selectedBranchId, isFranchiseUser } = useSession();
  // Franquicia: lo que le debe a la marca vive en su cuenta corriente (no en las compras).
  const mine = useQuery({ queryKey: ['franchise', 'mine'], queryFn: () => api.get<MyFranchiseDTO>('/franchise'), enabled: isFranchiseUser });
  const [params, setParams] = useSearchParams();
  const supplierId = params.get('proveedor') ?? '';
  const from = params.get('desde') ?? monthStart();
  const to = params.get('hasta') ?? '';
  const suppliers = useSuppliers();

  const setParam = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };

  const qs = new URLSearchParams();
  if (supplierId) qs.set('supplierId', supplierId);
  if (from) qs.set('from', from);
  if (to) qs.set('to', to);
  const purchases = useQuery({
    queryKey: ['purchases', { supplierId, from, to }],
    queryFn: () => api.get<PurchaseRowDTO[]>(`/purchases?${qs.toString()}`),
    placeholderData: keepPreviousData,
  });

  const rows = purchases.data ?? [];
  const confirmed = rows.filter((r) => r.status === 'CONFIRMED');
  const total = confirmed.reduce((a, r) => a + r.totalCents, 0);
  const pending = confirmed.reduce((a, r) => a + (r.totalCents - r.paidCents), 0);
  const overdue = confirmed.filter((r) => r.totalCents > r.paidCents && r.dueDate && daysUntil(r.dueDate) < 0);

  return (
    <>
      <PageHeader
        title="Compras"
        subtitle="Cada compra entra al stock como lotes con su costo y vencimiento."
        actions={
          <Button variant="primary" icon={<Plus />} onClick={() => navigate('/compras/nueva')}>
            Nueva compra
          </Button>
        }
      />

      <div className="bf-kpi-grid">
        <Kpi
          label="Comprado en el período"
          value={formatMoney(total)}
          delta={`${confirmed.length} ${confirmed.length === 1 ? 'compra' : 'compras'}`}
          deltaMuted
        />
        <Kpi
          label="Pendiente de pago"
          value={formatMoney(pending)}
          tone="amber"
          delta={
            isFranchiseUser && mine.data && mine.data.balanceCents > 0 ? (
              <Link to="/mi-franquicia">Con la marca: {formatMoney(mine.data.balanceCents)} (cuenta corriente)</Link>
            ) : (
              'A proveedores, según el plazo de cada uno'
            )
          }
          deltaMuted
        />
        <Kpi
          label="Vencidas sin pagar"
          value={String(overdue.length)}
          tone={overdue.length > 0 ? 'red' : 'green'}
          delta={overdue.length > 0 ? formatMoney(overdue.reduce((a, r) => a + r.totalCents - r.paidCents, 0)) : 'Al día'}
          deltaMuted
        />
      </div>

      <Toolbar>
        <Select value={supplierId} onChange={(e) => setParam('proveedor', e.target.value)} aria-label="Proveedor">
          <option value="">Todos los proveedores</option>
          {suppliers.data?.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
        <label className="bf-cell-sub" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          Desde
          <Input type="date" value={from} onChange={(e) => setParam('desde', e.target.value)} style={{ width: 160 }} />
        </label>
        <label className="bf-cell-sub" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          Hasta
          <Input type="date" value={to} onChange={(e) => setParam('hasta', e.target.value)} style={{ width: 160 }} />
        </label>
      </Toolbar>

      <Card>
        <CardBody flush>
          {purchases.isPending ? (
            <div style={{ padding: '8px 22px 16px', display: 'grid', gap: 12 }}>
              <Skeleton />
              <Skeleton />
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<ReceiptText />}
              title="No hay compras en este período"
              text="Cargá la factura o el remito del proveedor y el stock se actualiza solo."
              action={
                <Button variant="primary" icon={<Plus />} onClick={() => navigate('/compras/nueva')}>
                  Nueva compra
                </Button>
              }
            />
          ) : (
            <div className="bf-table-wrap">
              <table className="bf-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Proveedor</th>
                    {multiBranch && !selectedBranchId && <th>Sucursal</th>}
                    <th>Comprobante</th>
                    <th>Total</th>
                    <th>Vence</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const owed = r.totalCents - r.paidCents;
                    const late = r.status === 'CONFIRMED' && owed > 0 && r.dueDate && daysUntil(r.dueDate) < 0;
                    return (
                      <tr key={r.id} data-clickable="true" onClick={() => navigate(`/compras/${r.id}`)}>
                        <td className="bf-num">{formatDate(r.date)}</td>
                        <td>
                          <Link to={`/compras/${r.id}`} className="bf-cell-main" onClick={(e) => e.stopPropagation()}>
                            {r.supplierName}
                          </Link>
                          <div className="bf-cell-sub">
                            {r.items} {r.items === 1 ? 'producto' : 'productos'}
                          </div>
                        </td>
                        {multiBranch && !selectedBranchId && <td>{r.branchName}</td>}
                        <td className="bf-cell-sub">{[r.invoiceType, r.invoiceNumber].filter(Boolean).join(' ') || '—'}</td>
                        <td className="bf-num" style={{ fontWeight: 600 }}>
                          {formatMoney(r.totalCents)}
                        </td>
                        <td className={`bf-num ${late ? 'bf-text-danger' : 'bf-cell-sub'}`}>{r.dueDate ? formatDate(r.dueDate) : '—'}</td>
                        <td>
                          {r.status === 'VOIDED' ? (
                            <Badge tone="danger">Anulada</Badge>
                          ) : r.fromBrand ? (
                            <Badge tone="info">En cuenta corriente</Badge>
                          ) : owed <= 0 ? (
                            <Badge tone="success">Pagada</Badge>
                          ) : late ? (
                            <Badge tone="danger" dot>
                              Vencida
                            </Badge>
                          ) : (
                            <Badge tone="warning" dot>
                              A pagar
                            </Badge>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  );
}
